import { privacySections, quickSummaryItems, termsSections } from '../content/legal';

const PDF_FILENAME = 'localally-terms-and-privacy.pdf';
const PAGE_WIDTH = 595;
const PAGE_HEIGHT = 842;
const LEFT_MARGIN = 44;
const TOP_START = 800;
const LINE_HEIGHT = 14;
const MAX_CHARS = 88;

const escapePdfText = (value: string) =>
  value.replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)');

const wrapLine = (text: string, maxChars = MAX_CHARS) => {
  const words = String(text || '').split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = '';

  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (next.length > maxChars) {
      if (current) lines.push(current);
      current = word;
      return;
    }
    current = next;
  });

  if (current) lines.push(current);
  return lines;
};

const buildLegalLines = () => {
  const lines: Array<{ text: string; size?: number }> = [];

  lines.push({ text: 'LocalAlly Terms of Service and Privacy Policy', size: 16 });
  lines.push({ text: 'Quick Summary', size: 13 });
  quickSummaryItems.forEach((item) => {
    wrapLine(`${item.title}: ${item.body}`).forEach((line) => lines.push({ text: line }));
  });

  lines.push({ text: 'Terms of Service', size: 13 });
  termsSections.forEach((section) => {
    lines.push({ text: section.title, size: 12 });
    section.paragraphs?.forEach((paragraph) => {
      wrapLine(paragraph).forEach((line) => lines.push({ text: line }));
    });
    section.bullets?.forEach((bullet) => {
      wrapLine(`- ${bullet}`).forEach((line) => lines.push({ text: line }));
    });
  });

  lines.push({ text: 'Privacy Policy', size: 13 });
  privacySections.forEach((section) => {
    lines.push({ text: section.title, size: 12 });
    section.bullets?.forEach((bullet) => {
      wrapLine(`- ${bullet}`).forEach((line) => lines.push({ text: line }));
    });
  });

  return lines;
};

const buildPdf = () => {
  const lines = buildLegalLines();
  const pages: string[][] = [];
  let currentPage: string[] = [];
  let currentY = TOP_START;

  lines.forEach((line) => {
    const size = line.size || 10;
    const nextY = currentY - (line.size ? LINE_HEIGHT + 4 : LINE_HEIGHT);
    if (nextY < 50) {
      pages.push(currentPage);
      currentPage = [];
      currentY = TOP_START;
    }
    currentPage.push(`BT /F1 ${size} Tf 1 0 0 1 ${LEFT_MARGIN} ${currentY} Tm (${escapePdfText(line.text)}) Tj ET`);
    currentY -= line.size ? LINE_HEIGHT + 4 : LINE_HEIGHT;
  });

  if (currentPage.length) pages.push(currentPage);

  const objects: string[] = [];
  objects.push('<< /Type /Catalog /Pages 2 0 R >>');

  const pageObjectNumbers = pages.map((_, index) => 4 + index * 2);
  objects.push(`<< /Type /Pages /Count ${pages.length} /Kids [${pageObjectNumbers.map((num) => `${num} 0 R`).join(' ')}] >>`);
  objects.push('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>');

  pages.forEach((pageLines, index) => {
    const pageObjectNumber = 4 + index * 2;
    const contentObjectNumber = pageObjectNumber + 1;
    const stream = pageLines.join('\n');
    objects.push(`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE_WIDTH} ${PAGE_HEIGHT}] /Resources << /Font << /F1 3 0 R >> >> /Contents ${contentObjectNumber} 0 R >>`);
    objects.push(`<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`);
  });

  let pdf = '%PDF-1.4\n';
  const offsets = [0];

  objects.forEach((object, index) => {
    offsets.push(pdf.length);
    pdf += `${index + 1} 0 obj\n${object}\nendobj\n`;
  });

  const xrefStart = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n`;
  pdf += '0000000000 65535 f \n';
  offsets.slice(1).forEach((offset) => {
    pdf += `${String(offset).padStart(10, '0')} 00000 n \n`;
  });
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefStart}\n%%EOF`;

  return pdf;
};

export const downloadLegalPdf = () => {
  const pdf = buildPdf();
  const blob = new Blob([pdf], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = PDF_FILENAME;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};
